import type { V2TranscriptWord } from "../../pages/V2Editor/V2TextEditor";

type TrackRecordLike = {
    id?: string | null;
    trackId?: string | null;
    activeTranscriptId?: string | null;
    duration?: number | null;
    transcriptionState?: string | null;
};

type SourceRangeLike = {
    inSec?: number | null;
    outSec?: number | null;
};

type SourceSegmentLike = {
    id?: string | null;
    offsetSec?: number | null;
    playbackRate?: number | null;
    range?: SourceRangeLike | null;
};

type AudioEntryLike = {
    assetId?: string | null;
    gain?: number | null;
    name?: string | null;
    color?: string | null;
    range?: SourceRangeLike | null;
    offsetSec?: number | null;
    playbackRate?: number | null;
    segments?: SourceSegmentLike[] | null;
};

type ScriptClipLike = {
    getAudioEntries?: () => AudioEntryLike[];
    scriptCuts?: any[] | null;
    gaps?: any[] | null;
    scriptSegments?: any[] | null;
    segments?: any[] | null;
    transcriptHiddenRanges?: any[] | null;
    wordCorrections?: any[] | null;
};

type NormalizedPlacedSourceSegment = {
    offsetSec: number;
    playbackRate: number;
    rangeInSec: number;
    rangeOutSec: number;
};

export type ScriptTranscriptWord = V2TranscriptWord & {
    speakerId?: string;
    sourceAssetId?: string;
    sourceGain?: number;
    energyRatio?: number;
};

type ScriptTranscriptTokenKind = "word" | "audio_event";

export type BuildScriptTranscriptWordsOptions = {
    scriptClip: ScriptClipLike;
    trackRecords?: TrackRecordLike[] | null;
    fetchTranscriptForTrack: (args: {
        assetId: string;
        transcriptId: string;
        sourceIndex: number;
        trackRecord?: TrackRecordLike;
    }) => Promise<any | undefined>;
    includeAudioEvents?: boolean;
    cutFilterMode?: "none" | "visible" | "playable";
};

const MIN_ENERGY_RATIO = 0.2;

const toFiniteNumber = (value: any, fallback: number): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const normalizePlacedSourceSegments = (
    entry: AudioEntryLike,
    sourceDurationSec?: number | null
): NormalizedPlacedSourceSegment[] => {
    const out: NormalizedPlacedSourceSegment[] = [];
    const entryAny = entry as any;
    const entrySegments: SourceSegmentLike[] | undefined =
        Array.isArray(entryAny?.segments) && entryAny.segments.length > 0 ? entryAny.segments : undefined;

    const normalizeOutSec = (rawOutSec: any, inSec: number): number => {
        const sourceDuration = Number(sourceDurationSec);
        if (rawOutSec == null) {
            if (Number.isFinite(sourceDuration) && sourceDuration > inSec) {
                return sourceDuration;
            }
            return Number.POSITIVE_INFINITY;
        }

        const outSec = Number(rawOutSec);
        if (!Number.isFinite(outSec)) {
            if (Number.isFinite(sourceDuration) && sourceDuration > inSec) {
                return sourceDuration;
            }
            return Number.POSITIVE_INFINITY;
        }

        return outSec;
    };

    const pushSegment = (segmentLike: SourceSegmentLike | AudioEntryLike) => {
        const offsetSec = Number((segmentLike as any)?.offsetSec ?? 0);
        const playbackRate = Number((segmentLike as any)?.playbackRate ?? entryAny?.playbackRate ?? 1.0) || 1.0;
        const rangeInSec = Number((segmentLike as any)?.range?.inSec ?? entryAny?.range?.inSec ?? 0);
        const rangeOutSec = normalizeOutSec((segmentLike as any)?.range?.outSec ?? entryAny?.range?.outSec, rangeInSec);
        if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) return;
        if (!Number.isFinite(rangeInSec)) return;
        if (!(rangeOutSec === Number.POSITIVE_INFINITY || (Number.isFinite(rangeOutSec) && rangeOutSec > rangeInSec))) return;
        out.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
    };

    if (entrySegments) {
        for (const seg of entrySegments) {
            pushSegment(seg);
        }
        return out;
    }

    pushSegment(entry);
    return out;
};

const findTrackRecordByAssetId = (
    trackRecords: TrackRecordLike[] | null | undefined,
    assetId: string
): TrackRecordLike | undefined => {
    if (!Array.isArray(trackRecords) || !assetId) return undefined;
    return trackRecords.find((t: any) => t?.id === assetId || t?.trackId === assetId);
};

const buildWordCorrectionMap = (scriptClip: ScriptClipLike): Map<string, string> => {
    const out = new Map<string, string>();
    const corrections: any[] = Array.isArray(scriptClip?.wordCorrections) ? scriptClip.wordCorrections : [];

    for (const correction of corrections) {
        if (correction == null) continue;
        const text = String(correction?.correctedText ?? "").trim();
        if (!text) continue;

        const sourceIndex = Number(correction?.sourceIndex);
        const srcStartSec = Number(correction?.srcStartSec);
        const srcEndSec = Number(correction?.srcEndSec);
        if (!Number.isFinite(sourceIndex) || !Number.isFinite(srcStartSec) || !Number.isFinite(srcEndSec)) continue;

        out.set(`${sourceIndex}:${srcStartSec}:${srcEndSec}`, text);
    }

    return out;
};

const isTimeInHiddenRange = (scriptClip: ScriptClipLike, timeSec: number): boolean => {
    const hiddenRanges: any[] = Array.isArray(scriptClip?.transcriptHiddenRanges) ? scriptClip.transcriptHiddenRanges : [];
    if (!Number.isFinite(timeSec) || hiddenRanges.length === 0) return false;

    for (const range of hiddenRanges) {
        const startSec = Number(range?.startSec ?? 0);
        const endSec = Number(range?.endSec ?? startSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) continue;
        if (timeSec >= startSec && timeSec < endSec) return true;
    }

    return false;
};

const isTimeInDeleteCut = (scriptClip: ScriptClipLike, timeSec: number): boolean => {
    const cuts: any[] = Array.isArray(scriptClip?.scriptCuts) ? scriptClip.scriptCuts : [];
    if (!Number.isFinite(timeSec) || cuts.length === 0) return false;

    for (const cut of cuts) {
        const startSec = Number(cut?.startSec ?? 0);
        const endSec = Number(cut?.endSec ?? startSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) continue;
        if (timeSec < startSec || timeSec >= endSec) continue;
        if (String(cut?.transcriptMode ?? "delete") === "delete") return true;
    }

    return false;
};

const isTimeInGap = (scriptClip: ScriptClipLike, timeSec: number): boolean => {
    const gaps: any[] = Array.isArray(scriptClip?.gaps) ? scriptClip.gaps : [];
    if (!Number.isFinite(timeSec) || gaps.length === 0) return false;

    for (const gap of gaps) {
        const startSec = Number(gap?.startSec ?? 0);
        const endSec = Number(gap?.endSec ?? startSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) continue;
        if (timeSec >= startSec && timeSec < endSec) return true;
    }

    return false;
};

const isWordInPlayableSection = (scriptClip: ScriptClipLike, clipStartSec: number, clipEndSec: number): boolean => {
    const scriptSegments: Array<{ srcStartSec?: number; srcEndSec?: number; isGap?: boolean }> =
        Array.isArray((scriptClip as any)?.scriptSegments) && (scriptClip as any).scriptSegments.length > 0
            ? (scriptClip as any).scriptSegments
            : Array.isArray((scriptClip as any)?.segments)
                ? (scriptClip as any).segments
                : [];

    if (scriptSegments.length === 0) return true;

    for (const seg of scriptSegments) {
        if (seg?.isGap) continue;
        const segStart = Number(seg?.srcStartSec ?? Number.NaN);
        const segEnd = Number(seg?.srcEndSec ?? Number.NaN);
        if (!Number.isFinite(segStart) || !Number.isFinite(segEnd) || segEnd <= segStart) continue;
        if (clipStartSec >= segStart && clipEndSec <= segEnd) {
            return true;
        }
    }

    return false;
};

const shouldIncludeByCutFilter = (
    scriptClip: ScriptClipLike,
    cutFilterMode: "none" | "visible" | "playable",
    wordStartSec: number,
    wordEndSec: number
): boolean => {
    if (cutFilterMode === "playable") {
        if (!isWordInPlayableSection(scriptClip, wordStartSec, wordEndSec)) return false;
    } else if (cutFilterMode === "visible") {
        if (isTimeInDeleteCut(scriptClip, wordStartSec)) return false;
        if (isTimeInGap(scriptClip, wordStartSec)) return false;
    }

    if (cutFilterMode !== "none" && isTimeInHiddenRange(scriptClip, wordStartSec)) {
        return false;
    }

    return true;
};

export const getScriptClipAudioEntriesSignature = (scriptClip: ScriptClipLike | null | undefined): string => {
    const entries = Array.isArray(scriptClip?.getAudioEntries?.()) ? scriptClip.getAudioEntries() : [];
    if (!entries.length) return "";

    const parts: string[] = [];
    for (let sourceIndex = 0; sourceIndex < entries.length; sourceIndex++) {
        const entry = entries[sourceIndex];
        const assetId = String(entry?.assetId ?? "");
        const gain = toFiniteNumber(entry?.gain, 1);
        const segments = normalizePlacedSourceSegments(entry ?? {});

        parts.push(`${sourceIndex}:${assetId}:${gain.toFixed(6)}:${segments.length}`);
        for (const seg of segments) {
            parts.push(
                `${seg.offsetSec.toFixed(6)}|${seg.rangeInSec.toFixed(6)}|${seg.rangeOutSec.toFixed(6)}|${seg.playbackRate.toFixed(6)}`
            );
        }
    }

    return parts.join(",");
};

export async function buildScriptTranscriptWords(
    options: BuildScriptTranscriptWordsOptions
): Promise<ScriptTranscriptWord[]> {
    const {
        scriptClip,
        trackRecords,
        fetchTranscriptForTrack,
        includeAudioEvents = false,
        cutFilterMode = "none",
    } = options;

    const audioEntries = Array.isArray(scriptClip?.getAudioEntries?.()) ? scriptClip.getAudioEntries() : [];
    if (!audioEntries.length) return [];

    const correctionByKey = buildWordCorrectionMap(scriptClip);
    const out: ScriptTranscriptWord[] = [];

    for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
        const entry = audioEntries[sourceIndex];
        const entryGain = Number(entry?.gain ?? 1);
        if (Number.isFinite(entryGain) && entryGain <= 0.001) continue;

        const assetId = String(entry?.assetId ?? "").trim();
        if (!assetId) continue;

        const trackRecord = findTrackRecordByAssetId(trackRecords, assetId);
        const transcriptId = String(trackRecord?.activeTranscriptId ?? "").trim();
        if (!transcriptId) continue;

        let transcriptJson: any | undefined;
        try {
            transcriptJson = await fetchTranscriptForTrack({ assetId, transcriptId, sourceIndex, trackRecord });
        } catch {
            transcriptJson = undefined;
        }

        if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) {
            continue;
        }

        const sourceDurationSec = Number(trackRecord?.duration ?? 0);
        const entrySegments = normalizePlacedSourceSegments(entry ?? {}, sourceDurationSec);
        if (!entrySegments.length) continue;

        for (const seg of entrySegments) {
            for (const rawWord of transcriptJson.words) {
                const tokenKind = String((rawWord as any)?.type ?? "word").trim() as ScriptTranscriptTokenKind;
                const includeToken = tokenKind === "word" || (includeAudioEvents && tokenKind === "audio_event");
                if (!includeToken) continue;

                const energyRatioRaw = Number(
                    (rawWord as any)?.energy?.ratio ??
                    (rawWord as any)?.energyRatio ??
                    (rawWord as any)?.energy_ratio ??
                    Number.NaN
                );
                const energyRatio = Number.isFinite(energyRatioRaw) ? energyRatioRaw : undefined;
                if (tokenKind === "word" && energyRatio != null && energyRatio > 0 && energyRatio < MIN_ENERGY_RATIO) {
                    continue;
                }

                const srcStartSec = toFiniteNumber((rawWord as any)?.t0 ?? (rawWord as any)?.start, 0);
                const srcEndSec = toFiniteNumber((rawWord as any)?.t1 ?? (rawWord as any)?.end, srcStartSec);
                if (srcEndSec < srcStartSec) continue;
                if (srcStartSec < seg.rangeInSec || srcEndSec > seg.rangeOutSec) continue;

                const startSec = (srcStartSec - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
                const endSec = (srcEndSec - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
                if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) continue;
                if (!shouldIncludeByCutFilter(scriptClip, cutFilterMode, startSec, endSec)) continue;

                const correctionKey = `${sourceIndex}:${srcStartSec}:${srcEndSec}`;
                const correctedText =
                    tokenKind === "word"
                        ? correctionByKey.get(correctionKey) ?? String((rawWord as any)?.text ?? "").trim()
                        : String((rawWord as any)?.text ?? "").trim();
                if (!correctedText) continue;

                const speakerIdRaw = (rawWord as any)?.speakerId ?? (rawWord as any)?.speaker_id;
                const speakerId = speakerIdRaw != null ? String(speakerIdRaw) : undefined;

                out.push({
                    text: correctedText,
                    kind: tokenKind,
                    startSec,
                    endSec,
                    srcStartSec,
                    srcEndSec,
                    sourceIndex,
                    speakerId,
                    sourceAssetId: assetId,
                    sourceGain: entryGain,
                    energyRatio,
                });
            }
        }
    }

    out.sort((a, b) => {
        const startDiff = Number(a?.startSec ?? 0) - Number(b?.startSec ?? 0);
        if (startDiff !== 0) return startDiff;

        const endDiff = Number(a?.endSec ?? 0) - Number(b?.endSec ?? 0);
        if (endDiff !== 0) return endDiff;

        return Number(a?.sourceIndex ?? 0) - Number(b?.sourceIndex ?? 0);
    });

    return out;
}
